import { useEffect, useRef } from 'react'

import type { Conversation, Message } from '@shared/api/types'

interface Options {
  conversation: Conversation | null | undefined
  historyReady: boolean
  hidden: boolean
  messages: readonly Pick<Message, 'seq'>[]
  visibleAnchor: number | null
  lastVisibleSeq: () => number | null
  markRead: (conversationId: string, seq: number, forceReceipt: boolean) => Promise<void>
}

/** Явное прочтение открытой ленты; вступление само по себе просмотром не считается. */
export function useReadReceipts({
  conversation,
  historyReady,
  hidden,
  messages,
  visibleAnchor,
  lastVisibleSeq,
  markRead,
}: Options): void {
  const channelReceiptRef = useRef<{ conversationId: string; seq: number } | null>(null)
  useEffect(() => {
    if (!conversation || !historyReady || hidden) return
    const broadcast = conversation.kind === 'channel' && conversation.channel?.format !== 'group'
    if (conversation.unread_count === 0 && !broadcast) return
    // У канала last_seq включает комментарии, которых нет в основной ленте.
    // Для просмотра постов достаточно загруженной истории и видимого seq.
    if (
      broadcast ? messages.length === 0 : !messages.some((message) => message.seq === conversation.last_seq)
    )
      return

    let timer: ReturnType<typeof setTimeout> | undefined
    const schedule = (): void => {
      clearTimeout(timer)
      if (document.visibilityState !== 'visible') return
      timer = setTimeout(() => {
        const seq = broadcast ? lastVisibleSeq() : conversation.last_seq
        if (!seq || seq > conversation.last_seq) return
        const previous = channelReceiptRef.current
        if (broadcast && previous?.conversationId === conversation.id && previous.seq >= seq) return
        const receipt = { conversationId: conversation.id, seq }
        if (broadcast) channelReceiptRef.current = receipt
        void markRead(conversation.id, seq, broadcast).catch(() => {
          if (channelReceiptRef.current === receipt) channelReceiptRef.current = null
        })
      }, 400)
    }
    schedule()
    document.addEventListener('visibilitychange', schedule)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', schedule)
    }
  }, [conversation, historyReady, hidden, messages, visibleAnchor, lastVisibleSeq, markRead])
}
